import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** Public Turnstile site key; without it the box is off (dev, tests, forks). */
export const turnstileSiteKey = () => process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
const loadScript = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const tag = document.createElement('script');
      tag.src = SCRIPT_SRC;
      tag.async = true;
      tag.onload = () => resolve(window.turnstile);
      tag.onerror = () => { scriptPromise = null; reject(new Error('turnstile script')); };
      document.head.appendChild(tag);
    });
  }
  return scriptPromise;
};

/**
 * Cloudflare Turnstile "I am not a robot" box. Calls onToken with the token
 * once solved and with '' when it expires or fails. Bump resetKey after a
 * submit: a token is good for one check only.
 */
function TurnstileBox({ onToken, resetKey = 0 }) {
  const { i18n } = useTranslation();
  const holder = useRef(null);
  const widget = useRef(null);
  const tokenCb = useRef(onToken);
  tokenCb.current = onToken;
  const siteKey = turnstileSiteKey();

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadScript().then((turnstile) => {
      if (cancelled || !holder.current || !turnstile) return;
      widget.current = turnstile.render(holder.current, {
        sitekey: siteKey,
        language: i18n.language || 'auto',
        callback: (token) => tokenCb.current(token),
        'expired-callback': () => tokenCb.current(''),
        'error-callback': () => tokenCb.current(''),
      });
    }).catch(() => tokenCb.current(''));
    return () => {
      cancelled = true;
      if (widget.current != null && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [i18n.language, siteKey]);

  useEffect(() => {
    if (resetKey && widget.current != null && window.turnstile) {
      window.turnstile.reset(widget.current);
      tokenCb.current('');
    }
  }, [resetKey]);

  if (!siteKey) return null;
  return <div ref={holder} className="flex justify-center min-h-[65px]" data-testid="turnstile-box" />;
}

export default TurnstileBox;
